import {
  PLAY_FLIGHT_MS,
  REVERSE_BEAT_MS,
  type HaftkhabisMove,
  type HaftkhabisView,
} from "@bing/engine-haftkhabis";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { Avatar } from "../components/Avatar";
import { Card } from "../components/Card";
import { GameIcon } from "../components/GameIcon";
import { shortName } from "../components/profile-ui";
import { SuitGlyph } from "../components/SuitGlyph";
import { cardEntryKeyframes, prefersReducedMotion, type SeatEdge } from "../game/card-motion";
import { DealerMarker } from "../game/dealer-marker";
import { FELT_STYLE, OWN_AVATAR, SEAT_AVATAR } from "../game/felt-geometry";
import { effectOf } from "../game/haftkhabis-effects";
import { HAND_FAN_BOX_H } from "../game/hand-fan";
import { handLayout, takeLayout } from "../game/haftkhabis-hand";
import {
  edgeOfSeat,
  type Anchor,
  type Arrival,
  type Flight,
  type RoundCardData,
} from "../game/haftkhabis-moments";
import {
  canArmTak,
  canDraw,
  canPass,
  canTake,
  choiceFor,
  playableCards,
  playMove,
  type CardPick,
} from "../game/haftkhabis-moves";
import { sortHand } from "../game/hand-sort";
import { heartsSeatFallback } from "../game/hearts-seat-label";
import { LIFTED_EXTRA_PX, LIFTED_TRANSITION, useLiftToPlay } from "../game/lift-to-play";
import { TurnTimer, TurnTimerSlot, type TableTurnTimer } from "../game/online-lifecycle";
import { presetAvatarFor } from "../game/seat-avatar";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatAvatar,
  type SeatIdentity,
  type SeatPlayer,
} from "../game/seat-identity";
import { noticeForSeat, type SeatNotice } from "../game/seat-notice";
import { BANNER_PILL } from "../game/table-notice";
import { StatusPill, TABLE_BTN, TrickStackIcon } from "../game/table-parts";
import { AVATAR_CORNER, TableSeat, TURN_GLOW, turnGlowSettle } from "../game/TableSeat";
import { useCompactTable, useMediaQuery } from "../game/use-compact-table";
import {
  useCatchable,
  useHaftkhabisFeed,
  useMoveFrameKey,
  type MomentClock,
} from "../game/use-haftkhabis-feed";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { getLiftToPlay } from "../settings/prefs";
import { playSfx } from "../sound/sfx";

/* ------------------------------------------------------------------ *
 * «هفت خبیث»'S TABLE ON THE SHARED FRAME (card-60) — پاسور's and بی‌دل's frame, header, felt, seats, strip and
 * hand, for a shedding game of individuals. The operator's brief is the acceptance test: clean and tidy,
 * and everything a player needs clear at a glance:
 *
 *   whose turn     the seat's steady gold glow, your ring and cards, the strip's pill;
 *   the direction  ONE mark between the pile and the discard, an arrow and its word, read off the DRAWN seats
 *                  (D27, `turnSpin`): still at rest, a reversal flipping it once, large;
 *   the centre     the face-down pile with its count, tappable exactly when `draw` is legal, and the
 *                  discard; beside it the suit a Jack named; on it, while a 7 chain lives, «جریمه: N کارت»;
 *   the counts     each seat's cards and minus points, on the seat, always; yours in your strip;
 *   every event    visibly, once and in its order (`haftkhabis-moments.ts`): THE TABLE DRAWS A MOMENT, NEVER A
 *                  FRAME: the card lands, then its effect and the counts; a sound at its moment, never a
 *                  frame's arrival (`SOUND.md`).
 *
 * THE LOAD (CLAUDE.md, «A MINI APP MUST PUT ALMOST NO LOAD ON THE PHONE»): no infinite animation, no rAF,
 * no interval, no polling. Finite animations and WAAPI flights that end; one timer, for a moment on screen
 * (`use-haftkhabis-feed.ts`), none while the app is hidden. Measuring is a ResizeObserver: it runs when
 * something changed size, and never otherwise.
 * ------------------------------------------------------------------ */

/** The header's top pad: the safe area through max(env, --tg-safe). */
const TOP_PAD = "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))";
/** The chat toggle's footprint, kept from the first frame online (پاسور's `CHAT_TOGGLE_PX`). */
const CHAT_TOGGLE_PX = 40;
/** A side move's own latch: a double tap sends once; past this a tap sends again, for a press or a catch
 *  nothing answered (refused, or lost with a socket): a side move that fails sends no frame back. No timer. */
const SIDE_REPEAT_MS = 1000;
/** The score box's columns, fixed as پاسور's are: Vazirmatn's digits are not tabular, «−۳۲۰» ≤ 37 px. */
const SCORE_COL = { you: 38, target: 28 } as const;

/** A side seat's column (پاسور's clamp, the same seats), and the top seat's. */
const SIDE_NAME_MAX = "clamp(52px, calc(min(50vw, 220px) - 128px), 92px)";
const TOP_NAME_MAX = "92px";
/*
 * TELEGRAM DESKTOP AT 150 % (a 256×393 window, `TD_ALL`'s last): the fan's 126 px box, your strip and the header
 * leave a felt too short for the top seat AND the centre, which went off its foot. So below 430 px of height (no
 * phone draws a table there) the top seat rides higher, the panel drops its foot's padding and the hand gives up the
 * foot of its cards past the window: their indexes and every tap target stay. `haftkhabis-layout-probes` at 256x393.
 */
const SEAT_POS: Record<Exclude<SeatEdge, "bottom">, string> = {
  top: "top-[38px] [@media(max-height:429.98px)]:top-5 left-1/2 -translate-x-1/2",
  // In Telegram Desktop's short window the side seats ride higher: the felt's row (a picker, the giver's
  // backs) needs the band under them (the window is what CSS can ask without measuring, بی‌دل's `YIELD_SHORT`).
  right:
    "top-[50%] [@media(max-height:499.98px)]:top-[42%] right-[calc(max(env(safe-area-inset-right),var(--tg-safe-right,0px))_+_16px)] -translate-y-1/2",
  left: "top-[50%] [@media(max-height:499.98px)]:top-[42%] left-[calc(max(env(safe-area-inset-left),var(--tg-safe-left,0px))_+_16px)] -translate-y-1/2",
};
/** Neutral rings: no teams here; the gold glow alone marks a turn (بی‌دل's). */
const NEUTRAL_RING = "0 0 0 2px rgb(var(--c-outline-variant)), 0 4px 8px rgba(0,0,0,.4)";
/** Caption-sized and neutral: a shorter name box, room for what a seat must say under it. */
const seatNameSkin = (compactPad: string | null): string =>
  `border-outline-variant bg-surface-container/90 ${compactPad ?? "px-2"} py-0.5 font-caption text-caption text-on-surface shadow-md`;

/** A chip under a seat's name: its cards, its minus points, a mark; one type and one height. */
const SEAT_CHIP =
  "m-0 inline-flex items-center gap-0.5 whitespace-nowrap rounded-full border border-solid border-secondary/40 bg-surface-container/90 px-1.5 font-caption text-caption leading-tight shadow-md";
/** The felt's bottom row: your prompt and the effects, one pill, one height (پاسور's rule). */
const FELT_PILL_TYPE =
  "m-0 py-1 font-label-md text-label-md max-[359px]:text-caption max-[359px]:font-bold";
const FELT_PILL_BOX =
  "max-w-full rounded-full border px-3.5 text-center shadow-md max-[359px]:px-2.5";
const FELT_PILL = `${FELT_PILL_TYPE} ${FELT_PILL_BOX}`;
const FELT_STRUT = `${FELT_PILL_TYPE} invisible w-0 -me-2`;
/** The one banner pill, allowed to wrap: under a text zoom a whole line beats a cut one. */
const EFFECT_PILL = `${BANNER_PILL.replace("truncate", "whitespace-normal")} max-[359px]:px-2.5 max-[359px]:text-caption max-[359px]:font-bold`;
/** A name in a line below 412 px: its first nine characters (بی‌دل's and پاسور's measured cap). */
const NAME_NARROW_QUERY = "(max-width: 411.98px)";
const NAME_NARROW = 9;

/*
 * THE CENTRE'S BLOCK AT SCALE 1, before the felt decides its scale: pile and discard side by side, the
 * `large` 50×70 card, the direction mark in the gap between them, and what hangs past their edges (the suit
 * chip, the count, the penalty). The felt's room is measured (`useFeltRoom`): the block shrinks to fit it and
 * grows on a roomy screen.
 */
const BLOCK = { w: 164, h: 90, cardW: 50, cardH: 70, gap: 40 } as const;
const SCALE_MIN = 0.52;
const SCALE_MAX = 1.35;
const CLEAR = 2;
const SEAT_INSET = 16;
/** What a flight rides (`card-throw`'s spirit, on an end that stops rather than creeps: the sound marks its stop). */
const FLIGHT_EASING = "cubic-bezier(.25,.55,.65,.9)";
/** The flying back's own width (`Card small`) and the turned card's (`Card large`). */
const SMALL_W = 36;
/** A back reaching a seat: this much of its size, sliding in under the avatar's edge. */
const SEAT_SCALE = 0.55;
/** Your dealt stack's cards at the fan's own size: 57.5 px wide. */
const STACK_SCALE = 57.5 / SMALL_W;

export interface FeltRoom {
  /** The table's own width (the hand's room). */
  readonly tableW: number;
  /** The centre block's scale and its middle's height in the felt. */
  readonly scale: number;
  readonly cy: number;
  /** The room left over the block, for a banner that cannot take the row. */
  readonly spare: number;
}

/**
 * THE ROOM THE FELT LEAVES THE CENTRE — measured, never a breakpoint (a WebView text zoom grows the top
 * seat and the row, and no media query can see it). Across: between the side seats' columns, whose cap is
 * `SIDE_NAME_MAX`'s clamp at this width (never the live box: a mark coming and going must not move the
 * cards). Down: under the top seat's box (its chips ride out of flow, so it is one height) and over the
 * row. The block sits at the felt's middle, level with the side seats, unless the room says otherwise.
 * The block's own contents move none of what is read, so a read cannot feed the next.
 */
function useFeltRoom(
  root: RefObject<HTMLElement>,
  felt: RefObject<HTMLElement>,
  row: RefObject<HTMLElement>,
): FeltRoom | null {
  const [room, setRoom] = useState<FeltRoom | null>(null);
  useLayoutEffect(() => {
    const rootEl = root.current;
    const feltEl = felt.current;
    if (!rootEl || !feltEl) return undefined;
    const read = (): void => {
      const f = feltEl.getBoundingClientRect();
      const tableW = rootEl.getBoundingClientRect().width;
      if (!(f.width > 0 && f.height > 0)) return;
      const under = (el: Element | null): number =>
        el ? el.getBoundingClientRect().bottom - f.top : 0;
      const topSeat = rootEl.querySelector("[data-top-seat]");
      const top = under(topSeat) + CLEAR;
      const rowTop = row.current ? row.current.getBoundingClientRect().top - f.top : f.height;
      const sideW = Math.min(92, Math.max(52, Math.min(tableW / 2, 220) - 128));
      const roomW = tableW - 2 * (SEAT_INSET + sideW + CLEAR);
      const roomH = rowTop - CLEAR - top;
      const scale = Math.max(
        SCALE_MIN,
        Math.min(SCALE_MAX, Math.min(roomW / BLOCK.w, roomH / BLOCK.h)),
      );
      const half = (BLOCK.h * scale) / 2;
      const cy = Math.max(top + half, Math.min(f.height / 2, rowTop - CLEAR - half));
      const spare = Math.max(0, rowTop - CLEAR - (cy + half));
      const next: FeltRoom = { tableW, scale, cy, spare };
      setRoom((prev) =>
        prev &&
        prev.tableW === next.tableW &&
        prev.scale === next.scale &&
        prev.cy === next.cy &&
        prev.spare === next.spare
          ? prev
          : next,
      );
    };
    read();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", read);
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    const topSeat = rootEl.querySelector("[data-top-seat]");
    for (const el of [rootEl, feltEl, row.current, topSeat]) if (el) ro?.observe(el);
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", read);
    };
  }, [root, felt, row]);
  return room;
}

/** Which way the turn runs round the felt, read off where the NEXT seat is drawn (D27). */
export function turnSpin(you: number, direction: 1 | -1): "against" | "with" {
  return edgeOfSeat((you + direction + 4) % 4, you) === "right" ? "against" : "with";
}

/**
 * THE DIRECTION: ONE MARK, BETWEEN THE PILE AND THE DISCARD: a bold curved arrow going the way the turn goes and its
 * word, so nobody has to be told what it is. Still at rest, no animation of its own. A 10: once, large enough to catch
 * the eye, the arrow grows on a dark disc over the cards, turns over to the other way, and settles (WAAPI on transform
 * and opacity; a mount never flips).
 */
function DirectionMark({
  spin,
  flip,
}: {
  spin: "against" | "with";
  flip: number | null;
}): ReactNode {
  const box = useRef<HTMLDivElement>(null);
  const disc = useRef<HTMLSpanElement>(null);
  const arrow = useRef<SVGSVGElement>(null);
  const flipped = useRef(flip);
  useLayoutEffect(() => {
    if (flip === flipped.current) return undefined;
    flipped.current = flip;
    if (flip === null) return undefined;
    const outer = box.current;
    const under = disc.current;
    const inner = arrow.current;
    if (!outer || !under || !inner || typeof outer.animate !== "function" || prefersReducedMotion())
      return undefined;
    const options = { duration: REVERSE_BEAT_MS, easing: "ease-in-out" } as const;
    const mirror = spin === "with" ? -1 : 1;
    const anims = [
      outer.animate(
        [
          { transform: "scale(1)" },
          { transform: "scale(2.3)", offset: 0.2 },
          { transform: "scale(2.3)", offset: 0.76 },
          { transform: "scale(1)" },
        ],
        options,
      ),
      under.animate(
        [{ opacity: 0 }, { opacity: 1, offset: 0.16 }, { opacity: 1, offset: 0.8 }, { opacity: 0 }],
        options,
      ),
      inner.animate(
        [
          { transform: `scaleX(${-mirror})` },
          { transform: `scaleX(${-mirror})`, offset: 0.22 },
          { transform: `scaleX(${mirror})`, offset: 0.62 },
          { transform: `scaleX(${mirror})` },
        ],
        options,
      ),
    ];
    return () => {
      for (const anim of anims) anim.cancel();
    };
  }, [flip, spin]);
  return (
    <div
      role="img"
      aria-label={fa.haftkhabis.directionLabel(spin === "against")}
      data-hk-direction={spin}
      className="relative z-[6] flex flex-none select-none flex-col items-center text-secondary"
      style={{ width: BLOCK.gap, height: BLOCK.cardH, paddingTop: 6 }}
    >
      <div ref={box} className="relative flex h-[30px] w-[30px] items-center justify-center">
        <span
          ref={disc}
          aria-hidden
          className="absolute -inset-1 rounded-full border border-solid border-secondary bg-surface-dim"
          style={{ opacity: 0 }}
        />
        <svg
          ref={arrow}
          width="30"
          height="30"
          viewBox="0 0 32 32"
          fill="none"
          aria-hidden="true"
          className="relative"
          style={{ display: "block", transform: spin === "with" ? "scaleX(-1)" : undefined }}
        >
          {/* against the clock: from the foot, up the right, over the top and down the left */}
          <path
            d="M19.8 26.3 A11 11 0 1 0 8.93 24.43"
            stroke="currentColor"
            strokeWidth="3.6"
            strokeLinecap="round"
          />
          <path d="M14.4 28.9 L4.6 28.6 L11.9 19.4 Z" fill="currentColor" />
        </svg>
      </div>
      <span
        aria-hidden="true"
        className="m-0 whitespace-nowrap font-bold leading-none"
        style={{ fontSize: 11 }}
      >
        {fa.haftkhabis.directionWord}
      </span>
    </div>
  );
}

/** A suit's ink on white paper: a card's own two (`Card`'s text face), whatever the theme. */
const suitInk = (suit: string): string =>
  suit === "hearts" || suit === "diamonds" ? "#c1121f" : "#111";

/** Where a card in the air is, asked of the DOM: a child's layout effect runs before its ancestors' refs attach. */
function anchorEl(table: Element, at: Anchor): Element | null {
  switch (at) {
    case "pile":
      return table.querySelector("[data-hk-pile]");
    case "discard":
      return table.querySelector("[data-hk-discard]");
    case "stack":
      return table.querySelector("[data-hk-stack]");
    case "take":
      return table.querySelector("[data-take-tapped]");
    default:
      return (
        table.querySelector(`[data-seat="${at}"] > div`) ??
        table.querySelector(`[data-own-ring="${at}"]`)
      );
  }
}

const centreOf = (r: DOMRect): { x: number; y: number } => ({
  x: r.left + r.width / 2,
  y: r.top + r.height / 2,
});

/**
 * One card in the air on WAAPI, from the moment's first instant (so its landing and its sound are one instant on one
 * clock), placed and flown once. It is hidden until it leaves; a back reaching a seat fades into it as it arrives, and
 * one that stays lies where it landed until the moment lets it go.
 */
function FlightCard({
  flight,
  clock,
  momentId,
}: {
  flight: Flight;
  clock: MomentClock;
  momentId: string;
}): ReactNode {
  const ref = useRef<HTMLSpanElement>(null);
  const backRef = useRef<HTMLSpanElement>(null);
  const faceRef = useRef<HTMLSpanElement>(null);
  const turning = flight.turn !== undefined;
  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    const table = el?.closest("[data-hk-table]");
    const from = table ? anchorEl(table, flight.from) : null;
    const to = table ? anchorEl(table, flight.to) : null;
    if (
      !el ||
      !parent ||
      !from ||
      !to ||
      typeof el.animate !== "function" ||
      prefersReducedMotion()
    ) {
      if (el && flight.stays !== true) el.style.display = "none";
      return undefined;
    }
    const o = parent.getBoundingClientRect();
    const a = from.getBoundingClientRect();
    const b = to.getBoundingClientRect();
    const start = centreOf(a);
    const end = centreOf(b);
    el.style.left = `${end.x - o.left}px`;
    el.style.top = `${end.y - o.top}px`;
    const own = turning ? BLOCK.cardW : SMALL_W;
    const scale0 = a.width / own;
    const scale1 = turning
      ? b.width / own
      : flight.to === "stack"
        ? STACK_SCALE
        : typeof flight.to === "number"
          ? SEAT_SCALE
          : b.width / own;
    const dx = start.x - end.x;
    const dy = start.y - end.y;
    const anims: Animation[] = [];
    const fading = !turning && flight.stays !== true;
    const fly = el.animate(
      [
        {
          transform: `translate(${dx}px, ${dy}px) scale(${scale0})`,
          opacity: 0, // not there until it leaves
        },
        // a turning card is whole from its first step; a back fades in as it leaves the pile
        { opacity: 1, offset: turning ? 0.01 : 0.1 },
        ...(fading ? [{ opacity: 1, offset: 0.82 }] : []),
        {
          transform: `translate(0px, 0px) scale(${scale1})`,
          opacity: fading ? 0 : 1,
        },
      ],
      {
        duration: flight.ms,
        delay: flight.at,
        easing: turning ? "ease-in-out" : FLIGHT_EASING,
        fill: "both",
      },
    );
    anims.push(fly);
    // THE TURN: its back narrows to nothing halfway there, and its face opens from there
    const half = flight.ms / 2;
    const first = flight.turn === "up" ? backRef.current : faceRef.current;
    const second = flight.turn === "up" ? faceRef.current : backRef.current;
    if (turning && first && second) {
      anims.push(
        first.animate([{ transform: "scaleX(1)" }, { transform: "scaleX(0)" }], {
          duration: half,
          delay: flight.at,
          easing: "ease-in",
          fill: "both",
        }),
        second.animate([{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], {
          duration: half,
          delay: flight.at + half,
          easing: "ease-out",
          fill: "both",
        }),
      );
    }
    const t0 = clock.t0(momentId);
    for (const anim of anims) anim.startTime = t0;
    return () => {
      for (const anim of anims) anim.cancel();
    };
    // mount-once: a flight is measured and flown when it appears
  }, []);
  const margin = turning
    ? { marginLeft: -BLOCK.cardW / 2, marginTop: -BLOCK.cardH / 2 }
    : { marginLeft: -SMALL_W / 2, marginTop: -25 };
  return (
    <span
      ref={ref}
      data-flight={flight.id}
      data-flight-to={String(flight.to)}
      className="absolute"
      style={{ left: 0, top: 0, ...margin, opacity: 0 }}
    >
      {turning ? (
        <span className="relative block" style={{ width: BLOCK.cardW, height: BLOCK.cardH }}>
          <span ref={backRef} className="absolute inset-0 block">
            <Card id="back" faceDown large />
          </span>
          <span ref={faceRef} className="absolute inset-0 block">
            <Card id={flight.face ?? "back"} large />
          </span>
        </span>
      ) : flight.face !== undefined ? (
        <Card id={flight.face} small />
      ) : (
        <Card id="back" faceDown small />
      )}
    </span>
  );
}

/**
 * The discard's top, flown in from the seat that played it on the moment's clock: `ThrownCard`'s frames, its landing
 * the instant the sound marks. No flight for a card that was already lying there.
 */
function FlownTop({
  edge,
  clock,
  momentId,
  children,
}: {
  edge: SeatEdge | null;
  clock: MomentClock;
  momentId: string | null;
  children: ReactNode;
}): ReactNode {
  const ref = useRef<HTMLSpanElement>(null);
  const flying = edge !== null && !prefersReducedMotion();
  useLayoutEffect(() => {
    const el = ref.current;
    if (!flying || !el || typeof el.animate !== "function") return undefined;
    const anim = el.animate(cardEntryKeyframes(edge), {
      duration: PLAY_FLIGHT_MS,
      easing: FLIGHT_EASING,
      fill: "both",
    });
    if (momentId !== null) anim.startTime = clock.t0(momentId);
    anim.onfinish = (): void => anim.cancel();
    return () => anim.cancel();
    // mount-once: keyed by the card, so a fresh top is a fresh mount
  }, []);
  return (
    <span ref={ref} className="block" {...(flying ? { "data-enter-from": edge } : {})}>
      {children}
    </span>
  );
}

/** A card named in a row of the round's card: its rank and suit's pip, in the card's ink. */
function MiniCard({ id }: { id: string }): ReactNode {
  const rank = id.charAt(0);
  const suit = { C: "clubs", D: "diamonds", H: "hearts", S: "spades" }[id.charAt(1)] ?? "spades";
  return (
    <span
      data-mini-card={id}
      className="m-0 inline-flex flex-none items-center rounded bg-white px-0.5 leading-none"
      style={{ fontSize: 11, fontWeight: 800, color: suitInk(suit) }}
      dir="ltr"
    >
      {rank === "T" ? "10" : rank}
      <SuitGlyph suit={suit} />
    </span>
  );
}

/**
 * THE ROUND'S CARD, over the felt: who went out and every seat's remaining hand first (`stage` «hands»), then the
 * table of points: its minus points, ×2 when a Jack finished, the cumulative column and the limit; ascending, the
 * fewest first, equal totals sharing a place (بی‌دل IS SCORED DOWNWARD). The two stages are one layout: the
 * points' columns hold their room from the start, so nothing moves when they show.
 */
function RoundCard({
  card,
  stage,
  you,
  nameOf,
}: {
  card: RoundCardData;
  stage: "hands" | "points";
  you: number;
  nameOf: (seat: number) => string;
}): ReactNode {
  const t = fa.haftkhabis;
  // THE CARD IS ONLY UP FOR ITS SPAN, SO IT NEVER SCROLLS: where the full table does not fit the felt (a
  // text zoom, a small screen, big hands) it steps down before the first paint: first no headings, and
  // only where that is not enough counts for hands.
  const boxRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<"full" | "dense" | "counts">("full");
  const dense = form !== "full";
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || form === "counts") return undefined;
    const read = (): void => {
      if (box.scrollHeight > box.clientHeight + 1) setForm(form === "full" ? "dense" : "counts");
    };
    read();
    // a late font or text zoom grows the contents, not the box: the observer watches what is inside
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    for (const el of box.children) ro?.observe(el);
    return () => ro?.disconnect();
  }, [form]);
  const totals = card.totalsAfter;
  const rows = totals
    .map((total, seat) => ({ seat, total }))
    .sort((a, b) => a.total - b.total || a.seat - b.seat);
  const rankOf = (total: number): string =>
    ["①", "②", "③", "④"][totals.filter((x) => x < total).length] ?? "④";
  const pointsShown = stage === "points";
  const pointCell: CSSProperties = pointsShown
    ? { animation: "pop-in-glow .35s ease" }
    : { visibility: "hidden" };
  return (
    <div
      data-testid="hk-round-card"
      data-round={card.round}
      data-stage={stage}
      className="absolute inset-3 z-[30] flex items-center justify-center rounded-[40px] bg-surface-dim/70 p-1"
      style={{ animation: "pop-in-glow .3s ease" }}
    >
      <div
        ref={boxRef}
        data-dense={dense ? form : undefined}
        className={`flex max-h-full w-full max-w-[360px] flex-col overflow-hidden rounded-2xl border border-solid border-secondary/60 bg-surface-container px-2.5 text-on-surface shadow-lg ${dense ? "py-1" : "gap-1 py-2"}`}
      >
        <p className="m-0 text-center font-label-md text-label-md text-secondary">
          {card.winner === you
            ? t.roundWonYou(card.round)
            : t.roundWon(nameOf(card.winner), card.round)}
        </p>
        {card.doubled && !dense ? (
          <p
            data-testid="hk-round-doubled"
            className="m-0 text-center font-caption text-caption font-bold text-danger-bluff"
          >
            {t.doubled}
          </p>
        ) : null}
        <table className="w-full border-collapse font-caption text-caption">
          {dense ? null : (
            <thead>
              <tr className="text-on-surface-variant">
                <th className="whitespace-nowrap p-0 text-start font-normal">{t.colPlayer}</th>
                <th className="whitespace-nowrap p-0 text-start font-normal">{t.colHand}</th>
                <th
                  className="whitespace-nowrap p-0 px-1 text-center font-normal"
                  style={pointCell}
                >
                  {t.colRound}
                </th>
                <th
                  className="whitespace-nowrap p-0 px-1 text-center font-normal"
                  style={pointCell}
                >
                  {t.colTotal}
                </th>
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map(({ seat, total }) => {
              const hand = card.hands[seat] ?? null;
              const points = card.points[seat] ?? 0;
              return (
                <tr
                  key={seat}
                  data-round-row={seat}
                  className={seat === you ? "font-bold text-primary" : undefined}
                >
                  <td className={`max-w-[92px] truncate p-0 align-middle ${dense ? "" : "py-0.5"}`}>
                    <span aria-hidden className="me-0.5 text-secondary" style={pointCell}>
                      {rankOf(total)}
                    </span>
                    <span dir="auto">{seat === you ? t.you : nameOf(seat)}</span>
                  </td>
                  <td className="p-0 py-0.5 align-middle" data-round-hand={seat}>
                    {form !== "counts" && hand !== null && hand.length > 0 ? (
                      <span className="flex flex-wrap items-center gap-0.5" dir="ltr">
                        {sortHand(hand).map((c) => (
                          <MiniCard key={c} id={c} />
                        ))}
                      </span>
                    ) : (
                      t.handCount(card.counts[seat] ?? 0)
                    )}
                  </td>
                  {/* doubled, the hand's own points ×۲: the figure with its mark is arithmetic that holds */}
                  <td
                    data-round-points={seat}
                    className="whitespace-nowrap p-0 px-1 py-0.5 text-center align-middle font-bold text-danger-bluff"
                    dir="ltr"
                    style={pointCell}
                  >
                    {t.minus(card.doubled ? points / 2 : points)}
                    {card.doubled && points > 0 ? (
                      <span className="ms-0.5">{t.doubledMark}</span>
                    ) : null}
                  </td>
                  <td
                    className="whitespace-nowrap p-0 px-1 py-0.5 text-center align-middle font-bold"
                    dir="ltr"
                    style={pointCell}
                  >
                    {t.minus(total)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {dense ? null : (
          <p className="m-0 text-center font-caption text-caption text-on-surface-variant">
            {`${t.scores} · ${t.target(card.targetScore)}`}
          </p>
        )}
      </div>
    </div>
  );
}

const ACT_DRAW = "@draw";
const ACT_PASS = "@pass";
const ACT_TAKE = "@take:";

export function HaftkhabisTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  players,
  seatNotices,
  timer,
  onInviteSeat,
  chat = false,
  liftToPlay = getLiftToPlay(),
  onHolding,
  resumed = false,
}: {
  view: HaftkhabisView;
  legal: readonly HaftkhabisMove[];
  onMove: (move: HaftkhabisMove) => void;
  onLeave: () => void;
  /** The signed-in user: YOUR seat with your real photo + name. */
  self?: SeatIdentity | null;
  /** The lobby roster: real occupants' names and faces; a bot keeps its positional stand-in. */
  players?: readonly SeatPlayer[];
  /** Online: what has just happened to each seat, said on that seat (`seat-notice.tsx`). */
  seatNotices?: readonly SeatNotice[] | undefined;
  /** The live turn countdown (online), in your strip's one fixed slot. */
  timer?: TableTurnTimer | null;
  /** The host's «+» on a bot chair (online, mid-game). */
  onInviteSeat?: ((seat: number) => void) | undefined;
  /** Online: the shell's chat toggle sits beside the exit; its footprint is kept from the first frame. */
  chat?: boolean;
  /** «تأیید قبل از بازی کارت» (`lift-to-play.ts`): a card tap lifts, a table tap plays. */
  liftToPlay?: boolean;
  /** Told whether a round's end is on screen: the containers hold the end card until it is not. */
  onHolding?: ((holding: boolean) => void) | undefined;
  /** A re-join onto a table in progress (never a fresh match's first frame): no deal is replayed. */
  resumed?: boolean;
}): ReactNode {
  const t = fa.haftkhabis;
  const you = view.you;
  const seats = view.seats;
  const compact = useCompactTable();
  const frameKey = useMoveFrameKey(view);

  const { scene, moment, busy, opening, holding, banner, clock } = useHaftkhabisFeed(
    view,
    timer?.turn.seat === you ? timer.turn : null,
    resumed,
  );
  const shown = scene.view;
  useEffect(() => {
    onHolding?.(holding);
    return () => onHolding?.(false);
  }, [holding, onHolding]);

  // Names and faces from ONE place, so a seat reads the same on the felt, in a banner and on the end card.
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, {
      you: t.you,
      fallback: (st) => heartsSeatFallback(st, you, seats, players),
    });
  const avatarOf = (seat: number): SeatAvatar =>
    resolveSeatAvatar(seat, you, self, players, presetAvatarFor);
  const nameNarrow = useMediaQuery(NAME_NARROW_QUERY);
  const lineName = (seat: number): string =>
    shortName(nameOf(seat), nameNarrow ? NAME_NARROW : undefined);
  const inviteFor = (seat: number): (() => void) | undefined =>
    onInviteSeat && players?.find((p) => p.seat === seat)?.isBot
      ? () => onInviteSeat(seat)
      : undefined;

  // What may be done is the server's list, while no moment holds the hand: a card still in the air, a
  // round's end, the deal.
  const moves = busy ? [] : legal;
  const playable = playableCards(moves);
  const onTurn = view.phase !== "gameOver" && view.turn === you && !busy;
  const yourMove = onTurn && moves.length > 0;

  // «تک کارت» armed for your next play (D15): it dies with that play. Keyed by the turn's step, so a
  // draw, a play or a lapse ends it, and a side frame (somebody's press) does not.
  const armKey = `${view.round}:${view.moveCount}`;
  const [armedAt, setArmedAt] = useState<string | null>(null);
  const armable = yourMove && canArmTak(moves);
  const armed = armable && armedAt === armKey;

  // THE PICKER (D21): a Jack's suit, a 2's seat. Client-side and cancellable; it lasts while its card is
  // on the list and asks the same question, whatever SIDE frames land meanwhile (somebody's press, a catch
  // adding a card). It belongs to this step of the turn, like the arm: a lapse ends it for good, and the
  // same card on the list next turn opens nothing by itself.
  const [pickerState, setPickerState] = useState<{
    card: string;
    kind: "suit" | "target";
    at: string;
  } | null>(null);
  const setPicker = (next: { card: string; kind: "suit" | "target" } | null): void =>
    setPickerState(next && { ...next, at: armKey });
  const choice =
    pickerState && pickerState.at === armKey ? choiceFor(moves, pickerState.card) : null;
  const picker = pickerState && choice?.kind === pickerState.kind ? pickerState : null;
  const pickRef = useRef<CardPick | null>(null);
  // the card your tap sent stands up at once, and leaves with its frame: the tap's own answer, on any network
  const [sent, setSent] = useState<{ card: string; frame: number } | null>(null);
  const sentCard = sent?.frame === frameKey ? sent.card : null;

  // Every send on the turn door through the shared lift's once-per-frame latch and its one «play» cue.
  const liftPlay = useLiftToPlay({
    enabled: liftToPlay,
    holding: busy,
    frameKey,
    playable,
    silent: true, // every card sounds where it lands (`haftkhabis-moments.ts`)
    onPlay: (token) => {
      // the server's own entry, as `playMove` forwards one: the plain draw comes first on the list (D14)
      if (token === ACT_DRAW)
        return onMove(moves.find((m) => m.type === "draw") ?? { type: "draw" });
      if (token === ACT_PASS) return onMove({ type: "pass" });
      if (token.startsWith(ACT_TAKE))
        return onMove({ type: "take", index: Number(token.slice(ACT_TAKE.length)) });
      const pick = pickRef.current;
      pickRef.current = null;
      const asks = choiceFor(moves, token);
      if (asks.kind === "suit" && pick?.suit === undefined) {
        setPicker({ card: token, kind: "suit" });
        return false; // no move yet: no latch, no cue
      }
      if (asks.kind === "target" && pick?.target === undefined) {
        setPicker({ card: token, kind: "target" });
        return false;
      }
      const move = playMove(moves, token, pick ?? {}, armed);
      if (!move) return false;
      setPicker(null);
      setSent({ card: token, frame: frameKey });
      onMove(move);
      return true;
    },
  });
  const tapCard = (card: string): void => {
    if (picker?.card === card) {
      setPicker(null); // the same card again: back to the hand
      if (liftToPlay) liftPlay.tapCard(card);
      return;
    }
    setPicker(null);
    if (liftToPlay) liftPlay.tapCard(card);
    else liftPlay.send(card);
  };
  /** «بازی کن»: the drawn card, through the same door as a tap on it (a Jack or a 2 still asks). */
  const tapDrawn = (card: string): void => {
    setPicker(null);
    liftPlay.send(card);
  };
  const answerPicker = (pick: CardPick): void => {
    if (!picker) return;
    pickRef.current = pick;
    if (!liftPlay.send(picker.card)) pickRef.current = null;
  };

  const drawable = canDraw(moves);
  const passable = canPass(moves);
  const takeable = canTake(moves);

  // «تک کارت» out of turn: your press, and the catches. A double tap sends once.
  // the frame says so AND the table has got there: your press comes up as your card lands, and goes as soon as the
  // frame has cleared it, whatever is still in the air
  const youOwe =
    (shown.owes.some((o) => o.seat === you) ? view.owes.find((o) => o.seat === you) : null) ?? null;
  const sideSent = useRef<{ key: string; at: number } | null>(null);
  const sendSide = (key: string, move: HaftkhabisMove): boolean => {
    const now = Date.now();
    const last = sideSent.current;
    if (last?.key === key && now - last.at < SIDE_REPEAT_MS) return false;
    sideSent.current = { key, at: now };
    onMove(move);
    return true;
  };
  const catchable = useCatchable(opening ? [] : view.owes, you);

  // Who sits where: the rule the flying cards and the waiting room read too.
  const opponents = Array.from({ length: seats }, (_u, seat) => seat).filter((s) => s !== you);
  const edgeOf = (seat: number): SeatEdge => edgeOfSeat(seat, you);
  const spin = turnSpin(you, shown.direction);

  // The felt's room.
  const rootRef = useRef<HTMLDivElement>(null);
  const feltRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const room = useFeltRoom(rootRef, feltRef, rowRef);
  const scale = room?.scale ?? 1;

  // The banner on screen, its words and its mark.
  const effect = banner ? effectOf(banner, you, lineName, banner.turnAt) : null;
  const markOn = (seat: number): ReactNode =>
    effect?.mark?.seat === seat ? (
      <span
        key={banner?.seq}
        data-seat-mark={seat}
        className={`m-0 block whitespace-nowrap rounded-full px-1.5 py-0.5 leading-none shadow-md ${
          effect.mark.tone === "bad"
            ? "bg-danger-bluff text-on-surface"
            : "bg-secondary text-ink-on-gold"
        }`}
        style={{ fontSize: 11, fontWeight: 800, animation: "pop-in-glow .3s ease" }}
      >
        {effect.mark.text}
      </span>
    ) : null;

  const pickingSeat =
    picker?.kind === "target" && choice?.kind === "target" ? choice.targets : null;

  /**
   * What a seat says besides its name, always: its CARDS, a small card with the count on the avatar's lower
   * left (out of flow: the seat's height is the room the felt has for the rest), and its MINUS POINTS, a
   * chip under the name or, on the top seat, beside the avatar (its one height is what the centre is
   * measured under). Then «تک کارت» either way, in flow: the seat grows only while it has that to say.
   */
  const seatExtra = (seat: number, top: boolean): ReactNode => {
    const count = shown.handCounts[seat] ?? 0;
    const total = shown.totalScores[seat] ?? 0;
    const owedCatch = !opening && catchable.has(seat) && shown.owes.some((o) => o.seat === seat);
    const ring = (compact ? SEAT_AVATAR.compact : SEAT_AVATAR.regular) + 4;
    const countBadge = (
      <span
        role="img"
        aria-label={t.seatCards(count)}
        data-seat-count={seat}
        className="absolute z-[1] flex min-w-[18px] items-center justify-center rounded border border-solid border-secondary bg-surface-container px-0.5 py-px font-caption text-caption font-bold leading-none text-on-surface shadow-md"
        style={{ top: ring - 16, right: "calc(50% + 6px)" }}
      >
        <span key={count} className="inline-block" style={{ animation: "score-bounce .5s ease" }}>
          {faDigits(count)}
        </span>
      </span>
    );
    const totalChip = (
      <span
        role="img"
        aria-label={t.seatTotal(total)}
        data-seat-total={seat}
        className={`${SEAT_CHIP} text-danger-bluff`}
        dir="ltr"
      >
        <span key={total} className="inline-block" style={{ animation: "score-bounce .5s ease" }}>
          {t.minus(total)}
        </span>
      </span>
    );
    // over the name (and the points, on a side seat) for its short time: a seat keeps one height, so
    // nothing around it moves (its wrapper places it, the button animates: a keyframe's transform would
    // take a `-translate` away)
    const catchButton = owedCatch ? (
      // a little past the side seat's narrow column: two lines of the words, not three
      <div className="absolute -inset-x-1 z-10 flex justify-center" style={{ top: ring + 2 }}>
        <button
          type="button"
          data-seat-catch={seat}
          aria-label={t.takOwedLabel(nameOf(seat))}
          onClick={() =>
            sendSide(`catch:${seat}:${view.owes.find((o) => o.seat === seat)?.seq}`, {
              type: "catch",
              target: seat,
            })
          }
          className={`${TABLE_BTN} ${top ? "whitespace-nowrap" : "whitespace-normal"} rounded-xl border border-solid border-secondary bg-secondary px-1.5 py-0.5 text-center font-caption text-caption font-bold leading-tight text-ink-on-gold shadow-md active:scale-95`}
          style={{ animation: "pop-in-glow .35s ease" }}
        >
          {t.takOwed}
        </button>
      </div>
    ) : null;
    const lit = pickingSeat?.includes(seat) ? (
      // the seat picker's tap: the whole seat, lit (a child of the seat's positioned root)
      <button
        type="button"
        data-seat-pick={seat}
        aria-label={t.pickSeatOf(nameOf(seat))}
        onClick={() => answerPicker({ target: seat })}
        className={`${TABLE_BTN} absolute -inset-1 z-20 rounded-2xl border-2 border-solid border-secondary bg-secondary/25 p-0`}
        style={{ boxShadow: "var(--card-legal-pulse-steady)" }}
      />
    ) : null;
    return (
      <>
        {countBadge}
        {top ? (
          <div className="absolute top-1.5 flex items-center" style={{ left: "calc(50% + 24px)" }}>
            {totalChip}
          </div>
        ) : (
          totalChip
        )}
        {catchButton}
        {lit}
      </>
    );
  };

  const dealerMark = (seat: number): ReactNode =>
    scene.round === null && shown.dealer === seat && shown.phase !== "gameOver" ? (
      <DealerMarker label={t.dealer} />
    ) : null;

  // YOUR HAND: sorted, the fan or two rows by its size (`haftkhabis-hand.ts`).
  const handCards = sortHand(shown.hand);
  const n = handCards.length;
  const layout = handLayout(n, room?.tableW ?? null);

  const ownName = nameOf(you);
  const ownAvatar = avatarOf(you);
  const ownTotal = shown.totalScores[you] ?? 0;
  const ownCount = shown.handCounts[you] ?? 0;
  const ownSafe = shown.safe.includes(you);
  // the pill says who the table is showing: the seat whose card is in the air, then whoever is on turn
  const pillSeat = scene.glow ?? shown.turn;
  const statusText = yourMove
    ? t.yourTurn
    : t.turnOf(pillSeat === you ? t.you : lineName(pillSeat));

  // The felt's row: your prompt on your move, who takes during a take, an effect's line otherwise.
  const pending = shown.pendingTake;
  const prompt: string | null = !yourMove
    ? pending !== null && !busy
      ? pending.giver === you
        ? t.takingFromYou(lineName(pending.target))
        : t.taking(lineName(pending.target))
      : null
    : shown.phase === "bonus"
      ? drawable
        ? t.bonusDraw
        : t.bonusPrompt
      : shown.penalty > 0
        ? playable.size > 0
          ? t.promptPenalty(shown.penalty)
          : t.promptPenaltyDraw(shown.penalty)
        : drawable
          ? t.promptDraw
          : takeable || passable
            ? null
            : t.promptPlay;
  // A row that holds controls keeps them: the line stands over it where the felt has the room for it.
  const rowBusy = picker !== null || passable;
  const bannerOver = effect !== null && rowBusy && (room?.spare ?? 0) >= 30;
  const bannerInRow = effect !== null && !rowBusy;
  // THE BLIND TAKE'S PANEL: up while it is yours to tap, and for the moment its card leaves
  const lastTake = useRef({ giver: 0, count: 0 });
  if (takeable && pending !== null)
    lastTake.current = { giver: pending.giver, count: shown.handCounts[pending.giver] ?? 0 };
  const takeRect = useRef<DOMRect | null>(null);

  const topPile = shown.pileCount > 0;
  // A Jack that ENDED a round named nothing (D10): no chip under the round's card, none on the last frame.
  const chosenSuit = scene.suit;
  const momentId = moment?.id ?? null;

  // YOUR HAND'S CARDS IN THE AIR: each flown to its own place in the fan, off the moment's clock. Over when they land;
  // a moment that is cut (a window opened, the app hidden) takes them with it.
  const arrivalAnims = useRef<Animation[]>([]);
  useLayoutEffect(
    () => () => {
      for (const anim of arrivalAnims.current) anim.cancel();
      arrivalAnims.current = [];
    },
    [momentId],
  );
  const arrivals: readonly Arrival[] = scene.arrivals;
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (arrivals.length === 0 || !root || momentId === null || prefersReducedMotion()) return;
    const t0 = clock.t0(momentId);
    for (const a of arrivals) {
      const el = root.querySelector<HTMLElement>(`[data-hand-card="${a.card}"]`);
      if (!el || typeof el.animate !== "function") continue;
      const from =
        a.from === "take" ? takeRect.current : anchorEl(root, a.from)?.getBoundingClientRect();
      if (!from) continue;
      const here = centreOf(el.getBoundingClientRect());
      const there = centreOf(from);
      const rest = el.style.transform;
      const anim = el.animate(
        [
          {
            transform: `translate(${there.x - here.x}px, ${there.y - here.y}px) ${rest}`,
            opacity: a.from === "stack" ? 1 : 0,
          },
          { opacity: 1, offset: 0.12 },
          { transform: rest, opacity: 1 },
        ],
        { duration: a.ms, delay: a.at, easing: FLIGHT_EASING, fill: "backwards" },
      );
      anim.startTime = t0;
      arrivalAnims.current.push(anim);
    }
  }, [arrivals, momentId, clock]);
  const ownRingAnim = yourMove ? turnGlowSettle("playing", view.moveCount) : undefined;
  const ownGlow = yourMove || scene.glow === you;
  const glowPile: CSSProperties = drawable
    ? {
        boxShadow: "var(--card-legal-pulse-steady)",
        animation: "card-legal-pulse-settle 1.35s ease-in-out forwards",
      }
    : {};

  return (
    <div
      ref={rootRef}
      data-hk-table=""
      className="relative mx-auto flex w-full max-w-[440px] flex-col overflow-hidden text-on-surface select-none"
      style={{
        height: "var(--game-h)",
        background:
          "radial-gradient(circle at center,rgb(var(--c-room-light)) 0%,rgb(var(--c-surface-dim)) 100%)",
      }}
    >
      {/* HUD: ONE 40 px band: the exit, the chat toggle's footprint online, the title, your total to the
          limit. Everybody else's total rides their own seat. */}
      <header className="relative z-40 px-4" style={{ paddingTop: TOP_PAD }}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={t.leave}
            onClick={onLeave}
            className={`${TABLE_BTN} flex h-10 w-10 flex-none self-start items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
          >
            <GameIcon type="exit" size={20} />
          </button>
          {chat ? (
            <div
              aria-hidden
              data-testid="chat-toggle-slot"
              className="h-10 flex-none"
              style={{ width: CHAT_TOGGLE_PX }}
            />
          ) : null}
          {/* The title leaves whole where it does not fit: an empty first line, 40 px tall, and the
              title beside it or, wider than the column, wrapped under it and clipped: never half a
              word (the box is the number that matters). */}
          <div className="pointer-events-none flex h-10 min-w-0 flex-1 flex-wrap items-center justify-center overflow-hidden">
            <span aria-hidden className="h-10 w-0" />
            <h1 className="m-0 whitespace-nowrap font-headline-md text-headline-md text-secondary max-[359px]:text-headline-sm">
              {t.title}
            </h1>
          </div>
          <div
            aria-label={t.matchScore}
            data-testid="hk-score-box"
            className="flex h-10 flex-none items-center gap-2 rounded-lg border border-outline-variant bg-surface-container/80 px-2 shadow-md"
          >
            <div
              className="flex flex-none flex-col items-center leading-none"
              style={{ minWidth: SCORE_COL.you }}
            >
              <span className="m-0 whitespace-nowrap font-caption text-caption text-on-surface-variant">
                {t.you}
              </span>
              <span
                key={ownTotal}
                data-own-total=""
                dir="ltr"
                className="m-0 whitespace-nowrap font-headline-sm text-headline-sm text-primary"
                style={{ animation: "score-bounce .5s ease" }}
              >
                {t.minus(ownTotal)}
              </span>
            </div>
            <div className="h-6 w-px flex-none bg-outline-variant" />
            <span
              className="m-0 flex-none whitespace-nowrap text-center font-caption text-caption text-on-surface-variant"
              style={{ minWidth: SCORE_COL.target }}
            >
              {t.target(shown.targetScore)}
            </span>
          </div>
        </div>
      </header>

      <main
        className="relative min-h-0 flex-1"
        onClick={liftToPlay ? liftPlay.tapTable : undefined}
      >
        <div
          ref={feltRef}
          data-hk-felt=""
          className="absolute inset-3 overflow-hidden rounded-[40px]"
          style={FELT_STYLE}
        >
          {/* THE CENTRE: physically the pile on the left, the discard on the right (`dir="ltr"`), its top-right
              corner free of the card's index, where the chosen suit sits. */}
          <div
            data-hk-centre=""
            data-scale={scale}
            className="absolute left-1/2"
            style={{
              width: BLOCK.w,
              height: BLOCK.h,
              top: room?.cy ?? "50%",
              transform: `translate(-50%, -50%) scale(${scale})`,
            }}
          >
            <div
              dir="ltr"
              className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center"
            >
              <button
                type="button"
                data-hk-pile=""
                data-tappable={drawable ? "1" : undefined}
                disabled={!drawable}
                aria-label={topPile ? t.pileLabel(shown.pileCount) : t.pileEmpty}
                onClick={() => liftPlay.send(ACT_DRAW)}
                className={`${TABLE_BTN} relative block rounded-md border-0 bg-transparent p-0 ${drawable ? "active:scale-95" : "cursor-default"}`}
                style={{ width: BLOCK.cardW, height: BLOCK.cardH, ...glowPile }}
              >
                {topPile ? (
                  <Card id="back" faceDown large />
                ) : (
                  <span
                    aria-hidden
                    className="block h-full w-full rounded-md border-2 border-dashed border-secondary/40"
                  />
                )}
                <span
                  data-hk-pile-count=""
                  className={`${SEAT_CHIP} absolute -top-2 left-1/2 -translate-x-1/2 text-on-surface`}
                >
                  {faDigits(shown.pileCount)}
                </span>
              </button>
              <DirectionMark spin={spin} flip={scene.flip} />
              <div
                className="relative"
                style={{ width: BLOCK.cardW, height: BLOCK.cardH }}
                data-hk-discard={scene.top ?? ""}
              >
                {scene.top === null ? (
                  // the table is clear: the discard's place, empty, until the first card is turned onto it
                  <span
                    aria-hidden
                    data-hk-discard-empty=""
                    className="block h-full w-full rounded-md border-2 border-dashed border-secondary/40"
                  />
                ) : null}
                {scene.under !== null && scene.under !== scene.top ? (
                  <span
                    aria-hidden
                    className="absolute inset-0"
                    style={{ transform: "rotate(-6deg)" }}
                  >
                    <Card id={scene.under} large />
                  </span>
                ) : null}
                {scene.top !== null ? (
                  <span className="absolute inset-0">
                    {/* keyed by the card, never by what lies under it: a reshuffle or the end of a moment moves
                        the discards, and the top must not fly in again */}
                    <FlownTop
                      key={`${shown.round}:${scene.top}`}
                      edge={scene.topFrom}
                      clock={clock}
                      momentId={momentId}
                    >
                      <Card id={scene.top} large />
                    </FlownTop>
                  </span>
                ) : null}
                {chosenSuit !== null ? (
                  <span
                    role="img"
                    aria-label={t.chosenSuit(t.suits[chosenSuit])}
                    data-hk-suit={chosenSuit}
                    className="absolute -right-3 -top-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-solid border-secondary bg-white shadow-lg"
                    style={{
                      fontSize: 22,
                      color: suitInk(chosenSuit),
                      animation: "pop-in-glow .35s ease",
                    }}
                  >
                    <SuitGlyph suit={chosenSuit} />
                  </span>
                ) : null}
              </div>
              {shown.penalty > 0 ? (
                // ON the discard and the pile's foot, loud, the top card's index left showing. Its
                // wrapper places it, the chip animates: a keyframe's transform would take a `-translate` away
                <div className="absolute -bottom-2 left-1/2 z-10 -translate-x-1/2">
                  <span
                    key={shown.penalty}
                    data-hk-penalty={shown.penalty}
                    dir="rtl"
                    className="block whitespace-nowrap rounded-full border-2 border-solid border-white bg-danger-bluff px-2 py-0.5 font-label-md text-label-md text-white shadow-lg"
                    style={{ animation: "pop-in-glow .3s ease" }}
                  >
                    {t.penalty(shown.penalty)}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {/* THE FELT'S ROW — ONE row at its bottom, its height held by a strut. It holds, on your move, your
              prompt or the controls a move asks for (the Jack's four suits, the seat picker's way back, the
              drawn card's two answers, the giver's backs); otherwise whoever is taking; and an effect's
              line replaces a plain prompt for its short time. */}
          <div
            data-testid="felt-notices"
            className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex flex-col-reverse items-center gap-1 px-4 max-[299px]:px-2"
          >
            <div
              ref={rowRef}
              data-felt-row=""
              className="relative flex w-full flex-wrap items-center justify-center gap-2 gap-y-1"
            >
              <span aria-hidden="true" className={FELT_STRUT}>
                {"​"}
              </span>
              {picker?.kind === "suit" && choice?.kind === "suit" ? (
                <div
                  role="group"
                  aria-label={t.pickSuit}
                  data-hk-suit-picker=""
                  className="pointer-events-auto flex flex-wrap items-center justify-center gap-1.5 max-[299px]:gap-1"
                >
                  <span className="w-full text-center font-caption text-caption font-bold text-secondary [@media(max-height:589.98px)]:hidden">
                    {t.pickSuit}
                  </span>
                  {choice.suits.map((suit) => (
                    <button
                      key={suit}
                      type="button"
                      data-pick-suit={suit}
                      aria-label={t.suits[suit]}
                      onClick={() => answerPicker({ suit })}
                      className={`${TABLE_BTN} flex h-11 w-11 items-center justify-center rounded-xl border-2 border-solid border-secondary bg-white p-0 text-[28px] shadow-md active:scale-95 max-[299px]:h-8 max-[299px]:w-8 max-[299px]:text-[20px]`}
                      style={{ color: suitInk(suit) }}
                    >
                      <SuitGlyph suit={suit} />
                    </button>
                  ))}
                  <button
                    type="button"
                    data-pick-cancel=""
                    aria-label={t.cancel}
                    onClick={() => setPicker(null)}
                    className={`${TABLE_BTN} h-11 rounded-xl border border-solid border-outline-variant bg-surface-container px-2 font-caption text-caption text-on-surface active:scale-95 max-[359px]:w-11 max-[359px]:px-0 max-[299px]:h-8 max-[299px]:w-8`}
                  >
                    <span className="max-[359px]:hidden">{t.cancel}</span>
                    <span aria-hidden className="hidden max-[359px]:inline">
                      ✕
                    </span>
                  </button>
                </div>
              ) : picker?.kind === "target" ? (
                <div
                  data-hk-seat-picker=""
                  className="pointer-events-auto flex items-center justify-center gap-2"
                >
                  <span
                    className={`${FELT_PILL} border-secondary/70 bg-surface-container/90 text-secondary`}
                  >
                    {t.pickSeat}
                  </span>
                  <button
                    type="button"
                    data-pick-cancel=""
                    aria-label={t.cancel}
                    onClick={() => setPicker(null)}
                    className={`${TABLE_BTN} ${FELT_PILL} border-outline-variant bg-surface-container text-on-surface active:scale-95`}
                  >
                    <span className="max-[359px]:hidden">{t.cancel}</span>
                    <span aria-hidden className="hidden max-[359px]:inline">
                      ✕
                    </span>
                  </button>
                </div>
              ) : passable ? (
                <div data-hk-drawn="" className="pointer-events-auto flex items-center gap-2">
                  <button
                    type="button"
                    data-drawn-play=""
                    onClick={() => shown.drawnCard && tapDrawn(shown.drawnCard)}
                    className={`${TABLE_BTN} ${FELT_PILL} whitespace-nowrap border-secondary/70 bg-gradient-to-br from-secondary to-gold-deep text-ink-on-gold active:scale-95`}
                  >
                    {t.drawnPlay}
                  </button>
                  <button
                    type="button"
                    data-drawn-keep=""
                    onClick={() => liftPlay.send(ACT_PASS)}
                    className={`${TABLE_BTN} ${FELT_PILL} whitespace-nowrap border-outline-variant bg-surface-container text-on-surface active:scale-95`}
                  >
                    {t.drawnKeep}
                  </button>
                </div>
              ) : bannerInRow && effect ? (
                <span
                  key={banner?.seq}
                  data-hk-effect={banner?.kind}
                  className={EFFECT_PILL}
                  style={{ animation: "pop-in-glow .3s ease", fontWeight: 800 }}
                >
                  {effect.line}
                </span>
              ) : prompt !== null ? (
                <span
                  data-testid="hk-prompt"
                  className={`${FELT_PILL} ${
                    shown.phase === "bonus" && yourMove
                      ? "border-secondary bg-secondary text-ink-on-gold"
                      : "border-secondary/50 bg-surface-container/90 text-secondary"
                  }`}
                >
                  {prompt}
                </span>
              ) : null}
            </div>
            {bannerOver && effect ? (
              <span
                key={banner?.seq}
                data-hk-effect={banner?.kind}
                className={EFFECT_PILL}
                style={{ animation: "pop-in-glow .3s ease", fontWeight: 800 }}
              >
                {effect.line}
              </span>
            ) : null}
          </div>
        </div>

        {opponents.map((seat) => {
          const edge = edgeOf(seat);
          if (edge === "bottom") return null;
          const top = edge === "top";
          return (
            <TableSeat
              key={seat}
              seat={seat}
              posClass={SEAT_POS[edge]}
              avatar={avatarOf(seat)}
              name={nameOf(seat)}
              notice={noticeForSeat(seatNotices, seat)}
              onTurn={scene.glow === seat}
              compact={compact}
              onInvite={inviteFor(seat)}
              ringTone={NEUTRAL_RING}
              aboveAvatar={
                markOn(seat) ??
                (shown.safe.includes(seat) ? (
                  <span
                    data-seat-safe={seat}
                    className={`${SEAT_CHIP} border-secondary text-secondary`}
                  >
                    {t.tak}
                  </span>
                ) : null)
              }
              nameSkin={seatNameSkin}
              nameMax={top ? TOP_NAME_MAX : SIDE_NAME_MAX}
              wrapNotice={!top}
              markTopSeat={top}
              extra={seatExtra(seat, top)}
              avatarCorner={dealerMark(seat)}
            />
          );
        })}

        {scene.round ? (
          <RoundCard
            card={scene.round.card}
            stage={scene.round.stage}
            you={you}
            nameOf={lineName}
          />
        ) : null}
      </main>

      {/* Your panel: the strip, then your hand. */}
      <div
        className="relative z-30 flex flex-col gap-2 bg-gradient-to-t from-surface-container-lowest via-surface-container-lowest/80 to-transparent px-3 pt-2 [@media(max-height:429.98px)]:!pb-0"
        style={{ paddingBottom: "calc(14px + var(--game-safe-bottom))" }}
      >
        <div
          data-hk-strip=""
          className="relative flex min-h-[42px] items-center justify-between gap-2"
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div
              data-own-ring={you}
              className="relative flex flex-none items-center justify-center rounded-full"
              style={{
                padding: 2,
                boxSizing: "border-box",
                boxShadow: ownGlow ? TURN_GLOW : NEUTRAL_RING,
                animation: ownRingAnim,
              }}
            >
              {effect?.mark?.seat === you ? (
                <div
                  className="absolute left-1/2 -translate-x-1/2"
                  style={{ bottom: "calc(100% + 3px)" }}
                >
                  {markOn(you)}
                </div>
              ) : null}
              <Avatar
                avatarId={ownAvatar.avatarId}
                avatarPhotoId={ownAvatar.avatarPhotoId}
                size={compact ? OWN_AVATAR.compact : OWN_AVATAR.regular}
                official={ownAvatar.official}
              />
              {dealerMark(you) ? <div className={AVATAR_CORNER}>{dealerMark(you)}</div> : null}
            </div>
            <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 leading-tight">
              <span
                dir="auto"
                className={`m-0 inline-block truncate ${compact ? "font-body-lg text-body-lg" : "font-headline-sm text-headline-sm"} text-primary`}
                style={{ maxWidth: "min(168px, 100%)" }}
              >
                {ownName}
              </span>
              {/* one line: where it runs short the round's number wraps out of sight, never half a word */}
              <span
                className="m-0 flex w-full flex-wrap items-center gap-x-1.5 overflow-hidden whitespace-nowrap font-caption text-caption text-on-surface-variant"
                style={{ maxHeight: "1.5em" }}
              >
                <span data-own-count="" className="m-0 inline-flex items-center gap-0.5">
                  <span aria-hidden className="text-secondary">
                    <TrickStackIcon size={11} />
                  </span>
                  {t.cardsOf(ownCount)}
                </span>
                {ownSafe ? (
                  <span data-seat-safe={you} className="font-bold text-secondary">
                    {t.tak}
                  </span>
                ) : null}
                <span data-own-round="">{t.round(shown.round)}</span>
              </span>
            </div>
          </div>
          <div className="flex min-w-0 flex-initial items-center justify-end gap-2">
            {youOwe !== null && !opening ? (
              // AFTER AN UNPRESSED DESCENT: prominent until pressed, caught or closed (D15)
              <button
                type="button"
                data-tak-press=""
                onClick={() => {
                  // YOUR PRESS SOUNDS AS YOU PRESS: its frame only draws the bubble
                  if (sendSide(`press:${youOwe.seq}`, { type: "press" })) playSfx("bid");
                }}
                className={`${TABLE_BTN} flex-none whitespace-nowrap rounded-full border-2 border-solid border-white bg-danger-bluff px-3 py-1 font-label-md text-label-md text-white shadow-lg active:scale-95`}
                style={{ animation: "pop-in-glow .35s ease" }}
              >
                {t.takPress}
              </button>
            ) : armable ? (
              // BEFORE THE PLAY: it rides that play (`tak: true`), so no network can cost a careful player
              <button
                type="button"
                data-tak-arm=""
                aria-pressed={armed}
                onClick={() => {
                  if (!armed) playSfx("bid"); // the press, carried on the play to come
                  setArmedAt(armed ? null : armKey);
                }}
                className={`${TABLE_BTN} flex-none whitespace-nowrap rounded-full border-2 border-solid px-3 py-1 font-label-md text-label-md shadow-md active:scale-95 ${
                  armed
                    ? "border-secondary bg-secondary text-ink-on-gold"
                    : "border-secondary bg-surface-container text-secondary"
                }`}
              >
                {armed ? t.takArmed : t.tak}
              </button>
            ) : opening ? (
              <span aria-hidden="true" className="flex invisible">
                <StatusPill gold={false} still>
                  {t.yourTurn}
                </StatusPill>
              </span>
            ) : shown.phase === "gameOver" ? (
              <StatusPill gold={false} still truncate>
                {t.gameOver}
              </StatusPill>
            ) : (
              <StatusPill gold={yourMove} still truncate>
                {statusText}
              </StatusPill>
            )}
            <TurnTimerSlot timer={timer} hold={opening} />
          </div>
        </div>

        <div
          aria-label="your hand"
          data-hand-rows={layout.rows}
          className={`flex w-full justify-center overflow-visible [@media(max-height:429.98px)]:-mb-[26px] ${layout.rows === 2 ? "" : "px-2"}`}
        >
          <div
            className="relative"
            style={{ height: HAND_FAN_BOX_H, width: layout.width, maxWidth: "100%" }}
          >
            {/* where your dealt cards pile up, before they fan */}
            <span
              aria-hidden
              data-hk-stack=""
              className="pointer-events-none absolute left-1/2 top-1/2 h-0 w-0"
            />
            {handCards.map((card, i) => {
              const canPlay = playable.has(card);
              const isLifted = liftPlay.lifted === card;
              const isPicked = picker?.card === card;
              const isDrawn = shown.drawnCard === card;
              const up = isLifted || isPicked || isDrawn || sentCard === card;
              const rise = up
                ? 18 + LIFTED_EXTRA_PX
                : canPlay && liftPlay.lifted === null && picker === null
                  ? 18
                  : 0;
              const place = layout.place(i, rise);
              return (
                <div
                  key={`${shown.round}:${card}`}
                  data-hand-card={card}
                  data-legal={canPlay ? "1" : undefined}
                  className={`absolute ${place.bottom === undefined ? "bottom-0" : ""}`}
                  style={{
                    ...place,
                    zIndex: up ? 200 : layout.rowOf(i) * 100 + i,
                    transition: isLifted
                      ? LIFTED_TRANSITION
                      : "transform .2s cubic-bezier(.175,.885,.32,1.275)",
                  }}
                >
                  {/* a block: an inline wrapper's line box grows with a text zoom, up over your strip */}
                  <div className="block">
                    <Card
                      id={card}
                      large
                      // undimmed by the card's own opacity: in a fan a see-through card shows the ones
                      // under it. `dim`, below, is paint over it, and only on a move that has a choice
                      entered
                      disabled={!canPlay}
                      silent
                      selected={up}
                      lifted={isLifted}
                      resting={
                        (liftPlay.lifted !== null && !isLifted) || (picker !== null && !isPicked)
                      }
                      onClick={() => tapCard(card)}
                    />
                  </div>
                  {playable.size > 0 && !canPlay ? (
                    <span
                      aria-hidden="true"
                      data-dim=""
                      className="pointer-events-none absolute inset-0 rounded-md bg-black/50"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {takeable || scene.closingTake ? (
        <TakePanel
          count={lastTake.current.count}
          title={t.takeFrom(lineName(lastTake.current.giver))}
          closing={scene.closingTake}
          takeRect={takeRect}
          timer={timer?.turn.seat === you ? timer : null}
          onTake={(index) => liftPlay.send(`${ACT_TAKE}${index}`)}
        />
      ) : null}

      {moment !== null && moment.flights.length > 0 ? (
        <div
          key={moment.id}
          data-hk-flights=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[35]"
        >
          {moment.flights
            .filter((flight) => !scene.gone.includes(flight.id))
            .map((flight) => (
              <FlightCard key={flight.id} flight={flight} clock={clock} momentId={moment.id} />
            ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * THE BLIND TAKE (D13): the giver's cards, face down, big, in a clear panel titled with whose they are, for the 2's
 * target to tap one: the server picks whatever is tapped. Laid by `takeLayout` for the room the table leaves (over the
 * felt, and past it only where the felt cannot hold them): a tap target at any count. The tapped card lifts at once
 * and waits for its frame, then flies from there to your hand (`takeRect`).
 */
function TakePanel({
  count,
  title,
  closing,
  takeRect,
  timer,
  onTake,
}: {
  count: number;
  title: string;
  closing: boolean;
  takeRect: { current: DOMRect | null };
  /** Your window: where the panel covers your strip, its ring rides on the panel. */
  timer: TableTurnTimer | null;
  onTake: (index: number) => void;
}): ReactNode {
  const t = fa.haftkhabis;
  const boxRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const backsRef = useRef<HTMLDivElement>(null);
  const [tapped, setTapped] = useState<number | null>(null);
  const [room, setRoom] = useState<{
    w: number;
    feltTop: number;
    feltH: number;
    /** From the felt's top to the window's foot, and the whole window. */
    pastH: number;
    fullH: number;
    titleH: number;
  } | null>(null);
  useLayoutEffect(() => {
    // asked of the DOM: a child's layout effect runs before its ancestors' refs attach
    const root = boxRef.current?.closest("[data-hk-table]");
    const felt = root?.querySelector("[data-hk-felt]");
    if (!root || !felt) return undefined;
    const read = (): void => {
      const r = root.getBoundingClientRect();
      const f = felt.getBoundingClientRect();
      if (!(r.width > 0 && f.height > 0)) return;
      const titleH = titleRef.current?.getBoundingClientRect().height ?? 22;
      const next = {
        w: r.width - 2 * TAKE_EDGE - 2 * TAKE_PAD,
        feltTop: f.top - r.top,
        feltH: f.height,
        pastH: r.bottom - f.top - TAKE_EDGE,
        fullH: r.height - 2 * TAKE_EDGE,
        titleH,
      };
      setRoom((prev) =>
        prev &&
        prev.w === next.w &&
        prev.feltTop === next.feltTop &&
        prev.feltH === next.feltH &&
        prev.pastH === next.pastH &&
        prev.fullH === next.fullH &&
        prev.titleH === next.titleH
          ? prev
          : next,
      );
    };
    read();
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    for (const el of [root, felt, titleRef.current]) if (el) ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  const chrome = 2 * TAKE_PAD + (room?.titleH ?? 22) + TAKE_GAP;
  // over the felt; past it, over your own hand (a blind choice needs neither), only where the felt cannot hold them;
  // and in the smallest window over the whole table
  const roomW = room?.w ?? 320;
  const inFelt = takeLayout(count, roomW, (room?.feltH ?? 300) - chrome);
  const pastFelt = inFelt.fits ? inFelt : takeLayout(count, roomW, (room?.pastH ?? 400) - chrome);
  const layout = pastFelt.fits ? pastFelt : takeLayout(count, roomW, (room?.fullH ?? 500) - chrome);
  const panelH = chrome + layout.height;
  const top = room
    ? inFelt.fits
      ? room.feltTop + (room.feltH - panelH) / 2
      : pastFelt.fits
        ? room.feltTop
        : TAKE_EDGE
    : undefined;
  // a lapse takes a card nobody tapped: it leaves from the middle of the panel
  useLayoutEffect(() => {
    if (tapped === null) takeRect.current = backsRef.current?.getBoundingClientRect() ?? null;
  });
  return (
    <div
      ref={boxRef}
      role="group"
      aria-label={title}
      data-hk-take=""
      data-closing={closing ? "1" : undefined}
      data-over-table={pastFelt.fits ? undefined : "1"}
      className="absolute rounded-2xl border-2 border-solid border-secondary bg-surface-container/95 shadow-lg"
      style={{
        // over your hand; over the header too, where it starts above it
        zIndex: pastFelt.fits ? 33 : 45,
        left: TAKE_EDGE,
        right: TAKE_EDGE,
        top,
        padding: TAKE_PAD,
        opacity: closing ? 0 : 1,
        transition: "opacity .14s linear",
        pointerEvents: closing ? "none" : undefined,
      }}
    >
      {timer && !inFelt.fits ? (
        <div data-take-ring="" className="absolute" style={{ left: 2, top: 2 }}>
          <TurnTimer {...timer} compact />
        </div>
      ) : null}
      <p
        ref={titleRef}
        className="m-0 text-center font-label-md text-label-md font-bold text-secondary"
        style={{
          marginBottom: TAKE_GAP,
          paddingLeft: timer && !inFelt.fits ? TAKE_RING_ROOM : undefined,
        }}
      >
        {title}
      </p>
      <div
        ref={backsRef}
        dir="ltr"
        className="relative mx-auto"
        data-hk-backs={count}
        style={{ width: layout.width, height: layout.height }}
      >
        {Array.from({ length: count }, (_u, i) => {
          const place = layout.place(i);
          const lifted = tapped === i;
          return (
            <button
              key={i}
              type="button"
              data-take-back={i}
              data-take-tapped={lifted ? "1" : undefined}
              aria-label={t.takeCard(i + 1)}
              disabled={tapped !== null}
              onClick={(e) => {
                takeRect.current = e.currentTarget.getBoundingClientRect();
                setTapped(i);
                onTake(i);
              }}
              className={`${TABLE_BTN} absolute block rounded-md border-0 bg-transparent p-0`}
              style={{
                left: place.left,
                top: place.top,
                width: layout.cardW,
                height: layout.cardH,
                zIndex: lifted ? 100 : i,
                visibility: lifted && closing ? "hidden" : undefined,
                transform: lifted ? "translateY(-12px) scale(1.06)" : undefined,
                transition: "transform .12s ease-out",
                opacity: tapped !== null && !lifted ? 0.55 : 1,
                boxShadow: lifted ? "0 0 0 2px rgb(var(--c-secondary))" : undefined,
              }}
            >
              <span
                className="absolute left-0 top-0 block"
                style={{
                  transform: `scale(${layout.cardW / BLOCK.cardW})`,
                  transformOrigin: "top left",
                }}
              >
                <Card id="back" faceDown large />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The panel's distance from the table's edges, its padding, and the room under its title. */
const TAKE_EDGE = 8;
const TAKE_PAD = 10;
const TAKE_GAP = 8;
/** The title's room beside the ring riding on the panel (a compact ring is 32 px, less the panel's pad). */
const TAKE_RING_ROOM = 24;
